/** Video upload cap for Lip Sync pages (audio keeps `maxSizeMb`). */
export const DEFAULT_MAX_VIDEO_SIZE_MB = 100;

/** Minimum audio / recording length for main generator flows (all users). */
export const MIN_AUDIO_DURATION_SECONDS = 5;

/** Anonymous guests: max audio / recording length (seconds). */
export const ANON_MAX_AUDIO_DURATION_SECONDS = 20;

/**
 * Anonymous history-video preview cap (seconds), boundary B:
 * duration < 5 → 2s; ≥5 and <10 → 3s; ≥10 → 5s.
 * @param {number} durationSec
 * @returns {number|null} null when duration is unknown / invalid
 */
export function resolveAnonVideoPreviewLimitSeconds(durationSec) {
  const duration = Number(durationSec);
  if (!Number.isFinite(duration) || duration <= 0) return null;
  if (duration < 5) return 2;
  if (duration < 10) return 3;
  return 5;
}

/**
 * Effective max audio duration for upload / record / trim.
 * Logged-in users keep the model max; anonymous users are capped at 20s.
 */
export function resolveAudioMaxSeconds(limitOrMax, isLoggedIn) {
  const modelMax =
    typeof limitOrMax === "number"
      ? limitOrMax
      : Number(limitOrMax?.maxSeconds);
  const safeMax = Number.isFinite(modelMax) && modelMax > 0 ? modelMax : 100;
  if (isLoggedIn) return safeMax;
  return Math.min(safeMax, ANON_MAX_AUDIO_DURATION_SECONDS);
}

/**
 * Upload-step gate before trim / direct use.
 * Anonymous: file must already be within [min, max] (cannot open trim for longer audio).
 * Logged-in: may exceed max when `allowTrimOverMax` so they can trim down.
 * @returns {null | 'too_short' | 'too_long'}
 */
export function audioUploadDurationIssue(
  durationSec,
  {
    isLoggedIn,
    maxSeconds,
    allowTrimOverMax = false,
    minSeconds = MIN_AUDIO_DURATION_SECONDS,
  } = {},
) {
  const duration = Number(durationSec);
  if (!Number.isFinite(duration) || duration <= 0) return "too_short";
  if (duration < minSeconds) return "too_short";
  const modelMax = Number(maxSeconds);
  const safeMax =
    Number.isFinite(modelMax) && modelMax > 0
      ? modelMax
      : ANON_MAX_AUDIO_DURATION_SECONDS;
  if (Math.ceil(duration) > safeMax) {
    if (isLoggedIn && allowTrimOverMax) return null;
    return "too_long";
  }
  return null;
}

/** Minimum script length for text input (all users). */
export const MIN_SCRIPT_TEXT_LENGTH = 50;

/** Anonymous guests: max Input Text length (characters). */
export const ANON_MAX_SCRIPT_TEXT_LENGTH = 200;

/**
 * Counter / soft-limit display max for Input Text.
 * Logged-in: model max; anonymous: capped at 200.
 */
export function resolveScriptDisplayMaxLength(modelMax, isLoggedIn) {
  const n = Number(modelMax);
  const safeMax = Number.isFinite(n) && n > 0 ? n : 1100;
  if (isLoggedIn) return safeMax;
  return Math.min(safeMax, ANON_MAX_SCRIPT_TEXT_LENGTH);
}

export function resolveMaxVideoSizeMb(limit) {
  const n = Number(limit?.maxVideoSizeMb);
  if (Number.isFinite(n) && n > 0) return n;
  return DEFAULT_MAX_VIDEO_SIZE_MB;
}

export const MODELS = [
  {
    id: "lip_sync_1",
    /** API `generation_type` for create-task. */
    generationType: "lipsync1.0",
    name: "Lip Sync 1.0",
    badge: "Free",
    tagline: "Speed-optimized generator.",
    description:
      "Fast, low-cost synchronization for both video lip-sync and talking photos.",
    creditPerSecond: 1,
    maxSeconds: 100,
    maxTextLength: 1100,
    /** Audio / shared non-video size limit */
    maxSizeMb: 100,
    /** Video-only upload size limit */
    maxVideoSizeMb: 100,
    locked: false,
  },
  {
    id: "lip_sync_2",
    generationType: "lipsync2.0",
    name: "Lip Sync 2.0",
    badge: "Recommended",
    tagline: "Cinematic realism.",
    description:
      "Premium quality with natural expressions and ultra-realistic generation for all assets.",
    creditPerSecond: 5,
    maxSeconds: 40,
    maxTextLength: 360,
    maxSizeMb: 50,
    maxVideoSizeMb: 100,
    locked: true,
  },
];

export function generationTypeForModel(modelId) {
  const model = MODELS.find((item) => item.id === modelId);
  return model?.generationType ?? MODELS[0].generationType;
}

/** Resolve model id from API `generation_type` (history recreate). */
export function modelIdFromGenerationType(generationType) {
  const gt = String(generationType || "").toLowerCase();
  if (gt.includes("2") || gt.includes("lipsync2")) return "lip_sync_2";
  if (gt.includes("1") || gt.includes("lipsync1")) return "lip_sync_1";
  return null;
}
